import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Plus, MagnifyingGlass, Package, CheckCircle, Truck,
  ClipboardText, Clock, Warning, ArrowsClockwise, CaretRight, CloudArrowUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import ShipmentForm from "@/components/ShipmentForm";
import ReferenceAutocomplete from "@/components/ReferenceAutocomplete";
import { RiskPill } from "@/components/RiskCard";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";
import LoggerImportDialog from "@/components/LoggerImportDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  planned:    { bg: "bg-zinc-500/15", text: "text-zinc-300", dot: "bg-zinc-400", label: "Planned" },
  in_transit: { bg: "bg-teal-500/15", text: "text-teal-400", dot: "bg-teal-400", label: "In Transit" },
  delivered:  { bg: "bg-emerald-500/15", text: "text-emerald-400", dot: "bg-emerald-400", label: "Delivered" },
};

export default function ShipmentsView({ onSelectShipment }) {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [showLoggerImport, setShowLoggerImport] = useState(false);

  const fetchShipments = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (search) params.q = search;
      const res = await axios.get(`${API}/shipments/`, { params });
      setShipments(res.data || []);
    } catch (e) {
      toast.error("Failed to load shipments");
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => { fetchShipments(); }, [fetchShipments]);

  // Refetch on shipment lifecycle events from WS
  const refetchTimer = useRef(null);
  const handleLive = useCallback((msg) => {
    if (!["shipment.created", "shipment.started", "shipment.delivered"].includes(msg.type)) return;
    if (refetchTimer.current) return;
    refetchTimer.current = setTimeout(() => {
      refetchTimer.current = null;
      fetchShipments();
    }, 600);
  }, [fetchShipments]);
  useLiveUpdates(handleLive);

  const counts = {
    all: shipments.length,
    planned: shipments.filter(s => s.status === "planned").length,
    in_transit: shipments.filter(s => s.status === "in_transit").length,
    delivered: shipments.filter(s => s.status === "delivered").length,
  };

  return (
    <div className="space-y-4" data-testid="shipments-view">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold text-zinc-50 flex items-center gap-2">
            <Package size={20} weight="duotone" className="text-teal-400" />
            Shipments
          </h2>
          <Badge variant="outline" className="border-zinc-700 text-xs font-mono">{shipments.length}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowLoggerImport(true)}
            variant="outline"
            className="border-teal-700/50 text-teal-400 hover:bg-teal-500/10 text-xs h-8"
            data-testid="import-logger-btn">
            <CloudArrowUp size={14} weight="duotone" className="mr-1" /> Import Logger
          </Button>
          <Button onClick={() => setShowForm(true)}
            className="bg-teal-500 hover:bg-teal-600 text-zinc-950 text-xs h-8"
            data-testid="new-shipment-btn">
            <Plus size={14} className="mr-1" /> New Shipment
          </Button>
        </div>
      </div>

      {/* Filters + search */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <ReferenceAutocomplete value={search} onChange={setSearch} />
        </div>
        <div className="flex gap-1.5">
          {[
            { key: "all", label: "All" },
            { key: "planned", label: "Planned" },
            { key: "in_transit", label: "In Transit" },
            { key: "delivered", label: "Delivered" },
          ].map(f => (
            <Button key={f.key} variant={statusFilter === f.key ? "default" : "outline"} size="sm"
              onClick={() => setStatusFilter(f.key)}
              data-testid={`shipment-filter-${f.key}`}
              className={`text-xs h-9 ${statusFilter === f.key
                ? "bg-teal-500/20 text-teal-400 border-teal-800"
                : "border-zinc-700 hover:bg-zinc-800"}`}>
              {f.label}
              <span className="ml-1.5 text-[10px] opacity-70 font-mono">{counts[f.key]}</span>
            </Button>
          ))}
        </div>
        <button onClick={fetchShipments} className="ml-auto p-2 text-zinc-500 hover:text-teal-400 transition-colors"
          data-testid="shipment-refresh-btn">
          <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-xs font-mono text-zinc-500 animate-pulse text-center py-12">Loading shipments...</div>
      ) : shipments.length === 0 ? (
        <Card className="bg-zinc-900 border-zinc-800 p-12 text-center" data-testid="shipments-empty">
          <Package size={36} weight="duotone" className="text-zinc-700 mx-auto mb-3" />
          <p className="text-sm text-zinc-400 mb-1">No shipments yet</p>
          <p className="text-xs text-zinc-600 mb-4">Start tracking cargo by creating your first shipment.</p>
          <Button onClick={() => setShowForm(true)} className="bg-teal-500 hover:bg-teal-600 text-zinc-950 text-xs h-8">
            <Plus size={12} className="mr-1" /> Create Shipment
          </Button>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="shipments-list">
          {shipments.map(s => (
            <ShipmentCard key={s.id} shipment={s} onClick={() => onSelectShipment(s.id)} />
          ))}
        </div>
      )}

      <ShipmentForm open={showForm} onClose={() => setShowForm(false)}
        onCreated={(s) => {
          setShowForm(false);
          fetchShipments();
          toast.success(`Shipment "${s.name}" created`);
        }} />

      <LoggerImportDialog
        open={showLoggerImport}
        onClose={() => setShowLoggerImport(false)}
        onAttached={(summary) => {
          fetchShipments();
          if (summary?.shipment_id) {
            // Allow the toast to render before nav
            setTimeout(() => onSelectShipment?.(summary.shipment_id), 400);
          }
        }} />
    </div>
  );
}

function ShipmentCard({ shipment, onClick }) {
  const sev = STATUS_STYLES[shipment.status] || STATUS_STYLES.planned;
  const Icon = shipment.status === "delivered" ? CheckCircle : shipment.status === "in_transit" ? Truck : ClipboardText;
  const [risk, setRisk] = useState(null);

  useEffect(() => {
    let active = true;
    axios.get(`${API}/ai/shipment-risk/${shipment.id}`)
      .then((res) => { if (active) setRisk(res.data); })
      .catch(() => {});
    return () => { active = false; };
  }, [shipment.id]);

  return (
    <Card onClick={onClick}
      data-testid={`shipment-card-${shipment.id}`}
      className="bg-zinc-900 border-zinc-800 p-4 hover:bg-zinc-800/60 hover:border-zinc-700 cursor-pointer transition-colors">
      <div className="flex items-start gap-4">
        {/* Icon + status */}
        <div className={`p-2 rounded-sm ${sev.bg}`}>
          <Icon size={16} weight="duotone" className={sev.text} />
        </div>

        {/* Main */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-zinc-100 truncate">{shipment.name}</h3>
            <Badge className={`text-[9px] uppercase tracking-wider font-mono ${sev.bg} ${sev.text} border-0`}>
              {sev.label}
            </Badge>
            {risk && <RiskPill score={risk.risk_score} level={risk.risk_level} mode={risk.mode}
              testid={`ship-risk-pill-${shipment.id}`} />}
            {shipment.live_alert_count > 0 && (
              <Badge className="text-[9px] bg-red-500/20 text-red-400 border-0">
                <Warning size={9} className="mr-0.5" /> {shipment.live_alert_count} alerts
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            {shipment.customer && (
              <span className="text-xs text-zinc-400 font-mono">{shipment.customer}</span>
            )}
            {(shipment.references || []).slice(0, 3).map((r, i) => (
              <span key={i} className="text-[10px] font-mono text-zinc-500">
                <span className="text-zinc-600">{r.label || r.type}:</span> {r.value}
              </span>
            ))}
            {(shipment.references || []).length > 3 && (
              <span className="text-[10px] font-mono text-zinc-600">+{shipment.references.length - 3}</span>
            )}
          </div>
          <div className="flex items-center gap-4 mt-2 text-[10px] font-mono text-zinc-500">
            <span><Package size={10} className="inline mr-1" />{(shipment.device_names || []).length} device{(shipment.device_names || []).length !== 1 ? "s" : ""}</span>
            {shipment.driver_name && <span>👤 {shipment.driver_name}</span>}
            <span className="text-zinc-600">
              {shipment.temp_min}°F – {shipment.temp_max}°F
            </span>
            {shipment.start_time && (
              <span><Clock size={10} className="inline mr-1" />Started {new Date(shipment.start_time).toLocaleDateString()}</span>
            )}
          </div>
        </div>

        {/* Compliance + chevron */}
        <div className="flex items-center gap-3">
          {shipment.compliance_score != null && (
            <div className="text-right">
              <p className={`text-lg font-mono font-bold leading-none ${
                shipment.compliance_score >= 95 ? "text-emerald-400" :
                shipment.compliance_score >= 80 ? "text-amber-400" : "text-red-400"}`}>
                {shipment.compliance_score}%
              </p>
              <p className="text-[9px] uppercase tracking-wider text-zinc-500 mt-0.5">Compliance</p>
            </div>
          )}
          <CaretRight size={14} className="text-zinc-600" />
        </div>
      </div>
    </Card>
  );
}
